import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import styles from '../program-sheet.module.scss';
import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  doesFocusableExist,
  FocusContext,
  getCurrentFocusKey,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import Button from '@smart-tv/components/button/button';
import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import FavoriteFilled from '@smart-tv/components/icons/favorite-icons/favorite-filled';
import FavoriteFilledDisabled from '@smart-tv/components/icons/favorite-icons/favorite-filled-disabled';
import FavoriteOutlined from '@smart-tv/components/icons/favorite-icons/favorite-outlined';
import FavoriteOutlinedDisabled from '@smart-tv/components/icons/favorite-icons/favorite-outlined-disabled';
import Play from '@smart-tv/components/icons/play';
import { PlayCircleOutlinedIcon } from '@smart-tv/components/icons/play-circle-outlined';
import Record from '@smart-tv/components/icons/record';
import Replay from '@smart-tv/components/icons/replay';
import { useFavoritedContent } from '@smart-tv/hooks/use-favorited-content';
import { rootStore as stripesRootStore } from '@smart-tv/components/stripes/_stores/store-root';
import { FAVORITES_STRIPE_ID } from '@smart-tv/components/stripes/_utils/types';
import { isErrorDialogOpenAtom, pageTypeAtom } from '@smart-tv/atoms';
import testIds from '@smart-tv/e2e/shared/variables';

import { rootStore } from '../_stores/store-root';
import { uiStore } from '../_stores/store-ui';
import { useStreamActions } from '../_stores/useStreamActions';
import { isSheetDialogOpen } from '../_utils/focusKeyUtils';
import { FocusKeysSheet } from '../sheet-helpers';

import { CancelRecordIcon } from '@smart-tv/components/icons/cancel-recording';
import { DeleteIcon } from '@smart-tv/components/icons/delete';

import ProgressSpinner from '@smart-tv/components/progress-spinner/progress-spinner';
import useNPVRSheet from '@smart-tv/hooks/use-npvr';
import { NPVRStreamAtom } from '@smart-tv/atoms';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';

const actionTestIds: Partial<Record<FocusKeysSheet, string>> = {
  [FocusKeysSheet.FOCUS_KEY_WATCH_LIVE]: testIds.programsheet.buttons.play,
  [FocusKeysSheet.FOCUS_KEY_CATCH_UP]: testIds.programsheet.buttons.replay,
  [FocusKeysSheet.FOCUS_KEY_RECORD]: testIds.programsheet.buttons.record,
  [FocusKeysSheet.FOCUS_KEY_FAVORITE]: testIds.programsheet.buttons.favorite,
  [FocusKeysSheet.FOCUS_KEY_DELETE_RECORDING]: testIds.programsheet.buttons.deleteRecording
};

const getTestIdForAction = (action: string) => {
  const id = actionTestIds[action as FocusKeysSheet];
  return id !== undefined ? id : '';
};

const iconForAction = (
  action: string,
  isFavorited: boolean,
  isFavoriteBeingSet: boolean,
  isRecording: boolean,
  isRecordingBeingSet: boolean,
  isDeletingRecording: boolean
): React.ReactNode => {
  switch (action) {
    case FocusKeysSheet.FOCUS_KEY_WATCH_LIVE:
      return <Play />;
    case FocusKeysSheet.FOCUS_KEY_CATCH_UP:
      return <Replay />;
    case FocusKeysSheet.FOCUS_KEY_RECORD:
      return isRecordingBeingSet ? (
        <ProgressSpinner icon={true} />
      ) : isRecording ? (
        <CancelRecordIcon />
      ) : (
        <Record />
      );
    case FocusKeysSheet.FOCUS_KEY_DELETE_RECORDING:
      return isDeletingRecording ? <ProgressSpinner icon={true} /> : <DeleteIcon />;
    case FocusKeysSheet.FOCUS_KEY_FAVORITE:
      if (isFavoriteBeingSet)
        return isFavorited ? <FavoriteFilledDisabled /> : <FavoriteOutlinedDisabled />;
      return isFavorited ? <FavoriteFilled /> : <FavoriteOutlined />;
    case FocusKeysSheet.FOCUS_KEY_TRAILER:
      return <PlayCircleOutlinedIcon />;
    default:
      return null;
  }
};

export const SheetActionsButtons = observer(() => {
  const { sheet } = rootStore.sheetStore;

  const { favoriteContent, unfavoriteContent } = useFavoritedContent();
  const { handlePlay, handleStartOver } = useStreamActions();
  const pageType = useAtomValue(pageTypeAtom);
  const setNPVRStream = useSetAtom(NPVRStreamAtom);
  const { player } = usePlayerContext();
  const errorOpen = useAtomValue(isErrorDialogOpenAtom);

  const {
    isRecording,
    isRecordingBeingSet,
    isDeletingRecording,
    handleToggleRecording,
    openDeleteRecordingDialog,
    mapContentTypeToEnum
  } = useNPVRSheet();

  const sheetButtons = sheet.buttons;

  const [isFavorited, setIsFavorited] = React.useState(sheet.isCustomerFavorite);
  const [isFavoriteBeingSet, setIsFavoriteBeingSet] = React.useState(false);

  const { ref, focusKey, focusSelf, hasFocusedChild } = useFocusable({
    trackChildren: true,
    autoRestoreFocus: true,
    isFocusBoundary: true,
    focusBoundaryDirections: ['up', 'left']
  });

  // Sync isFavorited when sheet.isCustomerFavorite changes (e.g., from external favorite/unfavorite actions)
  useEffect(() => {
    setIsFavorited(sheet.isCustomerFavorite);
  }, [sheet.isCustomerFavorite]);

  const hasSetInitialFocus = useRef(false);

  useEffect(() => {
    if (!sheetButtons || isSheetDialogOpen() || player || errorOpen) return;

    if (hasSetInitialFocus.current && doesFocusableExist(getCurrentFocusKey())) return;
    hasSetInitialFocus.current = true;

    const { primary, secondary } = sheetButtons;

    if (primary) {
      setFocus(primary.action);
    } else if (secondary.length === 1) {
      setFocus(secondary[0].action);
    } else {
      focusSelf();
    }
  }, [focusSelf, focusKey, sheetButtons, player, errorOpen]);

  const handleToggleFavorite = async () => {
    const contentTypeEnum = mapContentTypeToEnum(sheet.contentType);

    if (sheet.contentId && contentTypeEnum && sheet.title) {
      setIsFavoriteBeingSet(true);
      try {
        if (isFavorited) {
          await unfavoriteContent({
            id: sheet.contentId,
            type: contentTypeEnum,
            title: sheet.title
          });
        } else {
          await favoriteContent({
            id: sheet.contentId,
            type: contentTypeEnum,
            title: sheet.title
          });
        }

        setIsFavorited(!isFavorited);
        rootStore.sheetStore.setIsCustomerFavorite(!isFavorited);

        /*
        Check if favorites stripe exists by looking for stripe with id 13.
        This is a fragile way to check for the existence of the favorites stripe, but currently 
        there is no better way to do this. 
        
        If the favorites stripe exists, we trigger a refetch 
        of all stripes with update_on_favorite: true to update the favorites stripe. If it doesn't 
        exist and the content is being unfavorited, we trigger a full refresh of the stripes store 
        to update any stripes that may be showing favorited content. We only trigger a refresh on 
        unfavorite because if the content is being favorited, it wouldn't have been showing as 
        favorited yet in any case, so no need to refresh.
        - MRA
        */

        if (pageType === 'frontpage') {
          const favoritesStripeExists = stripesRootStore.stripesStore.uiElements.some(
            (element) => element.id === FAVORITES_STRIPE_ID
          );

          if (favoritesStripeExists) {
            stripesRootStore.stripesStore.refetchUpdateOnFavoriteStripes();
          } else if (!isFavorited) {
            stripesRootStore.stripesStore.refresh();
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsFavoriteBeingSet(false);
      }
    }
  };

  const handlePlayTrailer = () => {
    console.log('Play trailer');
  };

  const handleAction = (action: string) => {
    switch (action) {
      case FocusKeysSheet.FOCUS_KEY_WATCH_LIVE:
        setNPVRStream({
          episodeId: sheet.episodeId,
          focusKey: FocusKeysSheet.FOCUS_KEY_WATCH_LIVE,
          fromFocusEpisode: true
        });
        handlePlay();
        break;
      case FocusKeysSheet.FOCUS_KEY_CATCH_UP:
        handleStartOver();
        break;
      case FocusKeysSheet.FOCUS_KEY_RECORD:
        handleToggleRecording();
        break;
      case FocusKeysSheet.FOCUS_KEY_DELETE_RECORDING:
        openDeleteRecordingDialog();
        break;
      case FocusKeysSheet.FOCUS_KEY_FAVORITE:
        handleToggleFavorite();
        break;
      case FocusKeysSheet.FOCUS_KEY_TRAILER:
        handlePlayTrailer();
        break;
    }
  };

  const handleActionArrowPress = (
    direction: string,
    enterEpisodeSelector: boolean,
    focusSheetDescription: boolean
  ) => {
    if (
      direction === 'right' &&
      focusSheetDescription &&
      doesFocusableExist(FocusKeysSheet.FOCUS_KEY_SHEET_DESCRIPTION)
    ) {
      setFocus(FocusKeysSheet.FOCUS_KEY_SHEET_DESCRIPTION);
      return false;
    }

    if (
      direction === 'down' &&
      enterEpisodeSelector &&
      doesFocusableExist(FocusKeysSheet.EPISODE_SELECTOR)
    ) {
      setFocus(FocusKeysSheet.EPISODE_SELECTOR);
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (hasFocusedChild) {
      uiStore.toggleFocus(false);
    }
  }, [hasFocusedChild]);

  useEffect(() => {
    return () => uiStore.toggleRecordingDialog(false);
  }, []);

  if (!sheetButtons) return null;

  const useButtonStyle =
    (sheetButtons.secondary && sheetButtons.secondary.length === 1) ||
    (sheetButtons.secondary && sheetButtons.secondary.length === 2 && !sheetButtons.primary);

  const hasSecondaryActions = Boolean(sheetButtons.secondary?.length);

  return (
    <>
      <FocusContext.Provider value={focusKey}>
        <div className={styles['sheet-buttons']} ref={ref}>
          {sheetButtons.primary && (
            <Button
              onClick={() => handleAction(sheetButtons.primary.action)}
              data-testid={getTestIdForAction(sheetButtons.primary.action)}
              focusKey={sheetButtons.primary.action as FocusKeysSheet}
              icon={iconForAction(
                sheetButtons.primary.action,
                isFavorited,
                isFavoriteBeingSet,
                isRecording,
                isRecordingBeingSet,
                isDeletingRecording
              )}
              onArrowPress={(direction) =>
                handleActionArrowPress(direction, !hasSecondaryActions, true)
              }
              className={styles['sheet-buttons__button']}
            >
              {sheetButtons.primary.title}
            </Button>
          )}
          <div
            className={clsx(
              styles['sheet-buttons__secondary'],
              !useButtonStyle && styles['sheet-buttons__secondary--flex']
            )}
          >
            {sheetButtons.secondary &&
              sheetButtons.secondary.map((action, index) => {
                const actionFocusKey = action.action as FocusKeysSheet;
                const disabled =
                  (actionFocusKey === FocusKeysSheet.FOCUS_KEY_FAVORITE && isFavoriteBeingSet) ||
                  (actionFocusKey === FocusKeysSheet.FOCUS_KEY_RECORD && isRecordingBeingSet) ||
                  (actionFocusKey === FocusKeysSheet.FOCUS_KEY_DELETE_RECORDING &&
                    isDeletingRecording);
                const icon = iconForAction(
                  action.action,
                  isFavorited,
                  isFavoriteBeingSet,
                  isRecording,
                  isRecordingBeingSet,
                  isDeletingRecording
                );
                const isLastAction = index === sheetButtons.secondary.length - 1;

                if (useButtonStyle) {
                  return (
                    <Button
                      onClick={disabled ? undefined : () => handleAction(action.action)}
                      data-testid={getTestIdForAction(action.action)}
                      focusKey={actionFocusKey}
                      icon={icon}
                      style={{ width: '100%' }}
                      disabled={disabled}
                      key={action.action}
                      onArrowPress={(direction) =>
                        handleActionArrowPress(direction, isLastAction, true)
                      }
                      className={styles['sheet-buttons__button']}
                    >
                      {action.title}
                    </Button>
                  );
                }

                // OBS: the wrapping div is only here because IconButton doesn't support the className prop yet (hasn't been migrated)
                // - MRA
                return (
                  <div className={styles['sheet-buttons__icon-button']} key={action.action}>
                    <IconButton
                      onClick={disabled ? undefined : () => handleAction(action.action)}
                      data-testid={getTestIdForAction(action.action)}
                      focusKey={actionFocusKey}
                      icon={icon}
                      disabled={disabled}
                      background={true}
                      onArrowPress={(direction) =>
                        handleActionArrowPress(direction, isLastAction, isLastAction)
                      }
                      gutterRight={true}
                    >
                      {action.title}
                    </IconButton>
                  </div>
                );
              })}
          </div>
        </div>
      </FocusContext.Provider>
    </>
  );
});

export default SheetActionsButtons;
